<template>
  <PaginaAdministrar
    titulo="Saldos em Estoque"
    subtitulo="Consulta de saldos físicos e financeiros por filial e local"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="false"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <!-- Produto (Múltiplo) -->
        <div class="col-md-4">
          <CampoBuscaMultiplo
            id="filtroProdutos"
            label="Produtos"
            v-model="filtros.idsProdutos"
            v-model:itens-selecionados="filtros.produtosSelecionados"
            :configuracao-busca="configBuscaProduto"
            placeholder="Digite nome ou SKU..."
          />
        </div>
        <!-- Filial (Múltiplo) -->
        <div class="col-md-4">
          <CampoBuscaMultiplo
            id="filtroFiliais"
            label="Filiais"
            v-model="filtros.idsFiliais"
            v-model:itens-selecionados="filtros.filiaisSelecionados"
            :configuracao-busca="configBuscaFilial"
            placeholder="Selecione filiais..."
          />
        </div>
        <!-- Local Estoque (Múltiplo) -->
        <div class="col-md-4">
          <CampoBuscaMultiplo
            id="filtroLocaisEstoque"
            label="Locais de Estoque"
            v-model="filtros.idsLocaisEstoque"
            v-model:itens-selecionados="filtros.locaisEstoqueSelecionados"
            :configuracao-busca="configBuscaLocalEstoque"
            placeholder="Selecione locais..."
          />
        </div>
        <!-- Tipo de Local -->
        <div class="col-md-3">
          <CampoBuscaLista
            id="lstTipoLocal"
            label="Tipo de Local"
            tabela="LocalEstoque"
            v-model="filtros.lstTipoLocal"
          />
        </div>
        <!-- Checkboxes (alinhados verticalmente com o input do CampoBuscaLista de Tipo de Local) -->
        <div class="col-md-3">
          <label class="form-label invisible" aria-hidden="true">&nbsp;</label>
          <div
            class="d-flex gap-3 align-items-center mb-3"
            style="min-height: calc(1.5em + 0.75rem + 2px);"
          >
            <div class="form-check mb-0">
              <input class="form-check-input" type="checkbox" id="chkOcultarZero" v-model="filtros.ocultarSaldoZero">
              <label class="form-check-label" for="chkOcultarZero">Ocultar saldo zero</label>
            </div>
            <div class="form-check mb-0">
              <input class="form-check-input" type="checkbox" id="chkApenasDisponivel" v-model="filtros.apenasDisponivel">
              <label class="form-check-label" for="chkApenasDisponivel">Só disponível</label>
            </div>
          </div>
        </div>
        <!-- Botões -->
        <div class="col-md-12 text-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <!-- Erro -->
    <div v-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <!-- Summary Cards (KPIs) -->
    <div v-if="!erro" class="row g-3 mb-3 px-3">
      <div class="col-md-3">
        <div class="card border-0 shadow-sm bg-primary bg-opacity-10 h-100">
          <div class="card-body text-center py-3">
            <div class="fs-4 fw-bold">{{ formatarNumero(resumo.totalItens, 0) }}</div>
            <small class="text-muted">Itens em Estoque</small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card border-0 shadow-sm bg-success bg-opacity-10 h-100">
          <div class="card-body text-center py-3">
            <div class="fs-4 fw-bold">{{ formatarNumero(resumo.totalQtdEstoque, 4) }}</div>
            <small class="text-muted">Qtd Total em Estoque</small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card border-0 shadow-sm bg-warning bg-opacity-10 h-100">
          <div class="card-body text-center py-3">
            <div class="fs-4 fw-bold">{{ formatarMoeda(resumo.totalValorPadrao) }}</div>
            <small class="text-muted">Valor Total (BRL)</small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card border-0 shadow-sm bg-info bg-opacity-10 h-100">
          <div class="card-body text-center py-3">
            <div class="fs-4 fw-bold">{{ formatarMoedaGerencial(resumo.totalValorGerencial) }}</div>
            <small class="text-muted">Valor Total (USD)</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Grid -->
    <AdminTable
      v-if="!erro"
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum saldo encontrado."
      row-key="idSaldoEstoque"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      modulo-layout="SaldosEstoque"
      :colunas-padrao="colunasPadrao"
      :bloquear-primeira-coluna="false"
      :colunas-fixas="['skuProduto']"
      :exportando-progresso="exportandoProgresso"
      :dados-exportar="dadosExportar"
      titulo-exportacao="Saldos em Estoque"
      :filtros-exportacao="descricaoFiltros"
      @sort-change="handleSortChange"
      @show-notification="emitNotification"
      @exportar-dados="handleExportarDados"
    >
      <!-- SKU -->
      <template #skuProduto="{ row }">
        <span class="fw-bold text-danger small">{{ row.skuProduto || '-' }}</span>
      </template>
      <!-- Produto -->
      <template #nomeProduto="{ row }">
        <span class="fw-medium text-body-emphasis">{{ row.nomeProduto || '-' }}</span>
      </template>
      <!-- Filial -->
      <template #nomeFilial="{ row }">
        {{ row.nomeFilial || '-' }}
      </template>
      <!-- Local Estoque -->
      <template #nomeLocalEstoque="{ row }">
        {{ row.nomeLocalEstoque || '-' }}
      </template>
      <!-- Tipo Local -->
      <template #lstTipoLocal="{ row }">
        <span class="badge badge-ghost-secondary">{{ getTextoLista('LstTipoLocal', row.lstTipoLocal) }}</span>
      </template>
      <!-- Qtd Disponível com cor -->
      <template #quantidadeDisponivel="{ row }">
        <span class="badge" :class="badgeClasseDisponivel(row)">
          {{ formatarNumero(row.quantidadeDisponivel, 4) }}
        </span>
      </template>
      <!-- Qtd Reservada -->
      <template #quantidadeReservada="{ row }">
        <span class="text-warning fw-medium">
          {{ formatarNumero(row.quantidadeReservada, 4) }}
        </span>
      </template>
    </AdminTable>
  </PaginaAdministrar>
</template>

<script setup>
import { ref, reactive, computed, nextTick, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { usePaginacao } from '@/composables/usePaginacao'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import CampoBuscaMultiplo from '@/components/base/campos/CampoBuscaMultiplo.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import { criarConfigBuscaProduto } from '@/search-definitions/produtoSearch'
import { ST } from '@/constants/ST'
import { PROD_NATUREZA } from '@/constants/PROD_NATUREZA'
import { filialSearch } from '@/search-definitions/filialSearch'
import { criarConfigBuscaLocalEstoque } from '@/search-definitions/localEstoqueSearch'
import { formatarDataHoraBR } from '@/utils/useDataBR'

const emit = defineEmits(['show-notification', 'visualizar-item'])
const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const emitNotification = (p) => emit('show-notification', p)

// ============================================================
// Estado
// ============================================================
const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)
const listas = reactive({})
const isLoadingListas = ref(false)

// Controle de concorrência: cancela requisição anterior se uma nova for disparada
let abortController = null

// Estado da exportação
const exportandoProgresso = ref({ total: 0, carregados: 0 })
const dadosExportar = ref(null)

const sortField = ref('')
const sortOrder = ref('desc')

const filtros = reactive({
  idsProdutos: [],
  produtosSelecionados: [],
  idsFiliais: [],
  filiaisSelecionados: [],
  idsLocaisEstoque: [],
  locaisEstoqueSelecionados: [],
  lstTipoLocal: '',
  ocultarSaldoZero: false,
  apenasDisponivel: false
})

// Descrição textual dos filtros ativos para incluir na exportação
const descricaoFiltros = computed(() => {
  const partes = []
  if (filtros.produtosSelecionados?.length) {
    const nomes = filtros.produtosSelecionados.map(p => p.label || p.identificacao || p.nome).join(', ')
    partes.push(`Produtos: [${nomes}]`)
  }
  if (filtros.filiaisSelecionados?.length) {
    const nomes = filtros.filiaisSelecionados.map(f => f.label || f.identificacao || f.nome).join(', ')
    partes.push(`Filiais: [${nomes}]`)
  }
  if (filtros.locaisEstoqueSelecionados?.length) {
    const nomes = filtros.locaisEstoqueSelecionados.map(l => l.label || l.descricao || l.nome).join(', ')
    partes.push(`Locais: [${nomes}]`)
  }
  if (filtros.lstTipoLocal) {
    partes.push(`Tipo Local: ${filtros.lstTipoLocal}`)
  }
  if (filtros.ocultarSaldoZero) partes.push('Ocultar saldo zero')
  if (filtros.apenasDisponivel) partes.push('Só disponível')
  return partes.length ? partes.join(' | ') : 'Nenhum filtro aplicado'
})

const resumo = reactive({
  totalItens: 0,
  totalQtdEstoque: 0,
  totalValorPadrao: 0,
  totalValorGerencial: 0
})

// ============================================================
// Colunas
// ============================================================
const colunasPadrao = [
  // Grupo 1 — Produto
  { field: 'skuProduto', label: 'SKU', width: 120, sortable: true, align: 'left' },
  { field: 'nomeProduto', label: 'Produto', width: 250, sortable: true, align: 'left' },
  { field: 'codigoNcm', label: 'NCM', width: 100, sortable: false, align: 'left', visible: false },

  // Grupo 2 — Localização
  { field: 'nomeFilial', label: 'Filial', width: 160, sortable: true, align: 'left' },
  { field: 'nomeLocalEstoque', label: 'Local de Estoque', width: 180, sortable: true, align: 'left' },
  { field: 'lstTipoLocal', label: 'Tipo do Local', width: 130, sortable: false, align: 'center' },

  // Grupo 3 — Quantidades
  {
    field: 'quantidadeEmEstoque',
    label: 'Qtd em Estoque',
    width: 130,
    sortable: true,
    align: 'right',
    formatter: (val) => formatarNumero(val, 4)
  },
  {
    field: 'quantidadeDisponivel',
    label: 'Qtd Disponível',
    width: 130,
    sortable: true,
    align: 'right',
    formatter: (val) => formatarNumero(val, 4)
  },
  {
    field: 'quantidadeReservada',
    label: 'Qtd Reservada',
    width: 130,
    sortable: true,
    align: 'right',
    formatter: (val) => formatarNumero(val, 4)
  },

  // Grupo 4 — Custos Padrão (BRL)
  {
    field: 'custoMedioUnitPadrao',
    label: 'Custo Médio Unit. (BRL)',
    width: 160,
    sortable: true,
    align: 'right',
    formatter: (val) => formatarNumero(val, 6)
  },
  {
    field: 'custoMedioTotalPadrao',
    label: 'Custo Total (BRL)',
    width: 150,
    sortable: true,
    align: 'right',
    formatter: (val) => formatarNumero(val, 2)
  },

  // Grupo 5 — Custos Gerencial (USD)
  {
    field: 'custoMedioUnitGerencial',
    label: 'Custo Médio Unit. (USD)',
    width: 170,
    sortable: true,
    align: 'right',
    formatter: (val) => formatarNumero(val, 6),
    visible: false
  },
  {
    field: 'custoMedioTotalGerencial',
    label: 'Custo Total (USD)',
    width: 160,
    sortable: true,
    align: 'right',
    formatter: (val) => formatarNumero(val, 2),
    visible: false
  },

  // Grupo 6 — Auditoria
  {
    field: 'tsModificacao',
    label: 'Última Movimentação',
    width: 160,
    sortable: true,
    align: 'center',
    formatter: (val) => val ? formatarDataHoraBR(new Date(val)) : '-'
  }
]

// Mapeamento campo → nome do slot no template
// Apenas campos que possuem <template #xxx> no template precisam de slotName
const campoSlotMap = {
  skuProduto: 'skuProduto',
  nomeProduto: 'nomeProduto',
  nomeFilial: 'nomeFilial',
  nomeLocalEstoque: 'nomeLocalEstoque',
  lstTipoLocal: 'lstTipoLocal',
  quantidadeDisponivel: 'quantidadeDisponivel',
  quantidadeReservada: 'quantidadeReservada'
}

const colunas = computed(() => {
  return colunasPadrao.map(c => ({
    ...c,
    slotName: campoSlotMap[c.field] || undefined
  }))
})

// ============================================================
// Configurações de busca
// ============================================================
const configBuscaProduto = computed(() => {
  const base = criarConfigBuscaProduto(ST.Ativo, PROD_NATUREZA.ProdutoFisico)
  return {
    ...base,
    titulo: 'Buscar Produto',
    filtrosFixos: { ...base.filtrosFixos, idEmpresa: authStore.empresaAtiva }
  }
})

const configBuscaFilial = computed(() => ({
  ...filialSearch,
  titulo: 'Buscar Filial',
  filtrosFixos: { idEmpresa: authStore.empresaAtiva }
}))

const configBuscaLocalEstoque = computed(() => {
  // Correlação com o filtro de Filiais (múltiplo): só fixa a filial quando houver
  // exatamente UMA selecionada. Com múltiplas (ou nenhuma), NÃO restringe por filial,
  // para não esconder os locais das demais filiais selecionadas.
  // Tipos de local: [] = mostra todos (a consulta de Saldos não deve restringir a Trânsito).
  const base = criarConfigBuscaLocalEstoque(
    authStore.organizacaoAtiva,
    filtros.idsFiliais?.length === 1 ? filtros.idsFiliais[0] : undefined,
    []
  )
  return { ...base, titulo: 'Buscar Local de Estoque' }
})

// ============================================================
// Busca de dados
// ============================================================
async function buscarDados (pageNumber = 1) {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  // Cancela requisição anterior ainda em andamento para evitar race condition
  if (abortController) {
    abortController.abort()
  }
  abortController = new AbortController()

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)
    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (filtros.idsProdutos.length > 0) params.append('idsProdutos', filtros.idsProdutos.join(','))
    if (filtros.idsFiliais.length > 0) params.append('idsFiliais', filtros.idsFiliais.join(','))
    if (filtros.idsLocaisEstoque.length > 0) params.append('idsLocaisEstoque', filtros.idsLocaisEstoque.join(','))
    if (filtros.lstTipoLocal) params.append('lstTipoLocal', filtros.lstTipoLocal)
    if (filtros.ocultarSaldoZero) params.append('ocultarSaldoZero', 'true')
    if (filtros.apenasDisponivel) params.append('apenasDisponivel', 'true')
    if (sortField.value) params.append('sortBy', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/SaldosEstoque?${params.toString()}`, {
      signal: abortController.signal
    })
    lista.value = response.data
    atualizarPaginacao(response.headers)

    // Carrega resumo em paralelo
    await carregarResumo()
  } catch (error) {
    // Ignora erros de requisição cancelada (race condition controlada)
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') return
    erro.value = 'Não foi possível carregar os dados.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

async function carregarResumo () {
  try {
    const params = new URLSearchParams()
    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (filtros.idsProdutos.length > 0) params.append('idsProdutos', filtros.idsProdutos.join(','))
    if (filtros.idsFiliais.length > 0) params.append('idsFiliais', filtros.idsFiliais.join(','))
    if (filtros.idsLocaisEstoque.length > 0) params.append('idsLocaisEstoque', filtros.idsLocaisEstoque.join(','))
    if (filtros.lstTipoLocal) params.append('lstTipoLocal', filtros.lstTipoLocal)
    if (filtros.ocultarSaldoZero) params.append('ocultarSaldoZero', 'true')
    if (filtros.apenasDisponivel) params.append('apenasDisponivel', 'true')

    const response = await api.get(`/SaldosEstoque/resumo?${params.toString()}`)
    Object.assign(resumo, response.data || {})
  } catch (e) {
    console.error('[EstoqueSaldos] Erro ao carregar resumo:', e)
  }
}

// ============================================================
// Exportação
// ============================================================
async function handleExportarDados () {
  if (!authStore.empresaAtiva) {
    emitNotification({ message: 'Selecione uma empresa antes de exportar.', type: 'warning' })
    return
  }

  const totalRegistros = paginacao.value.totalCount ?? paginacao.value.TotalCount ?? 0
  if (!totalRegistros) {
    emitNotification({ message: 'Nenhum dado para exportar.', type: 'warning' })
    return
  }

  exportandoProgresso.value = { total: totalRegistros, carregados: 0 }
  dadosExportar.value = null

  const pageSize = paginacao.value.PageSize || 25
  const totalPages = Math.ceil(totalRegistros / pageSize)
  let todosDados = []

  try {
    for (let page = 1; page <= totalPages; page++) {
      const params = new URLSearchParams()
      params.append('pageNumber', page)
      params.append('pageSize', pageSize)

      if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
      if (filtros.idsProdutos.length > 0) params.append('idsProdutos', filtros.idsProdutos.join(','))
      if (filtros.idsFiliais.length > 0) params.append('idsFiliais', filtros.idsFiliais.join(','))
      if (filtros.idsLocaisEstoque.length > 0) params.append('idsLocaisEstoque', filtros.idsLocaisEstoque.join(','))
      if (filtros.lstTipoLocal) params.append('lstTipoLocal', filtros.lstTipoLocal)
      if (filtros.ocultarSaldoZero) params.append('ocultarSaldoZero', 'true')
      if (filtros.apenasDisponivel) params.append('apenasDisponivel', 'true')
      if (sortField.value) params.append('sortBy', sortField.value)
      if (sortOrder.value) params.append('sortOrder', sortOrder.value)

      const response = await api.get(`/SaldosEstoque?${params.toString()}`)
      const dados = response.data
      if (Array.isArray(dados)) {
        todosDados = todosDados.concat(dados)
      }

      exportandoProgresso.value = { total: totalRegistros, carregados: todosDados.length }
    }

    dadosExportar.value = [...todosDados]
    await nextTick()
    exportandoProgresso.value = { total: 0, carregados: 0 }
  } catch (e) {
    console.error('[EstoqueSaldos] Erro na exportação:', e)
    emitNotification({ message: 'Erro ao coletar dados para exportação.', type: 'error' })
    exportandoProgresso.value = { total: 0, carregados: 0 }
  }
}

// ============================================================
// Ações
// ============================================================
const aplicarFiltros = () => buscarDados(1)
const limparFiltros = () => {
  filtros.idsProdutos = []
  filtros.produtosSelecionados = []
  filtros.idsFiliais = []
  filtros.filiaisSelecionados = []
  filtros.idsLocaisEstoque = []
  filtros.locaisEstoqueSelecionados = []
  filtros.lstTipoLocal = ''
  filtros.ocultarSaldoZero = false
  filtros.apenasDisponivel = false
  buscarDados(1)
}
const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}
const mudarTamanhoPagina = (n) => {
  paginacao.value.PageSize = n
  buscarDados(1)
}
const handleSortChange = () => buscarDados(1)

// ============================================================
// Formatação
// ============================================================
function formatarNumero (valor, decimais = 2) {
  if (valor === null || valor === undefined) return '-'
  return Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: decimais, maximumFractionDigits: decimais })
}

function formatarMoeda (valor) {
  if (valor === null || valor === undefined) return '-'
  return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatarMoedaGerencial (valor) {
  if (valor === null || valor === undefined) return '-'
  return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'USD' })
}

function badgeClasseDisponivel (row) {
  if (row.quantidadeDisponivel > 0) return 'badge-ghost-success'
  if (row.quantidadeEmEstoque > 0) return 'badge-ghost-warning'
  if (row.quantidadeEmEstoque === 0) return 'badge-ghost-secondary'
  return 'badge-ghost-danger'
}

// ============================================================
// Resolução de Listas (Lst*)
// ============================================================
function getTextoLista (listaKey, id) {
  if (!id) return '-'
  const lista = listas[listaKey] || []
  const item = lista.find(i =>
    String(i.idListaSistemaItem) === String(id) ||
    String(i.idListaClienteItem) === String(id)
  )
  return item ? item.textoLista : id
}

const carregarListas = async () => {
  isLoadingListas.value = true
  try {
    const result = await metadataStore.carregarListasParaTabelas(['LocalEstoque'])
    Object.assign(listas, result)
  } catch (error) {
    console.error('Erro ao carregar listas auxiliares:', error)
  } finally {
    isLoadingListas.value = false
  }
}

// ============================================================
// Ciclo de vida
// ============================================================
watch(() => authStore.empresaAtiva, async (n) => {
  if (n) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
    Object.keys(listas).forEach(k => delete listas[k])
  }
})

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})
</script>

<style scoped>
</style>
